import type {
  JsonData,
  JsonDataItem,
  OdinKey,
  VisibilityButtonSetting,
  VisibilityFieldSettingType,
  VisibilityFormField,
} from '../types';
import { isArrayOf } from '../utils';
import type { VisibilityCondition } from './visibility-condition.interfaces';
import type { VisibilityConditionBase } from './visibility-condition-base';

/**
 * Form sections are fields of this type. Unrelated to the `GROUP` visibility
 * condition type, which means a user-group membership check.
 */
const SECTION_FIELD_TYPE = 'GROUP';

/**
 * Minimal lookup interface for the field-settings flat map.
 * Using a custom interface instead of ReadonlyMap avoids variance issues
 * when odin-components passes Map<React.Key, DynamicFormField> (React.Key
 * includes bigint, while OdinKey is string | number).
 */
interface FieldSettingsLookup {
  has(key: OdinKey): boolean;
  get(key: OdinKey): VisibilityFormField | undefined;
}

export class FieldSetting<
  TFieldSetting extends VisibilityFieldSettingType = VisibilityFieldSettingType,
> {
  private _hasSettingChanged = false;
  private fieldSetting: TFieldSetting;
  private conditionGroups: Array<Array<VisibilityConditionBase>>;
  private fieldSettingsFlatMap?: FieldSettingsLookup;

  public get hasSettingChanged() {
    return this._hasSettingChanged;
  }

  public get isButtonSetting(): boolean {
    return this.isFormButton(this.fieldSetting);
  }

  /**
   * Sections always evaluate their conditions against the source field's value, even when that
   * source field is hidden. Fields and buttons are force-hidden instead. This asymmetry is
   * deliberate and mirrors Viking (see SectionVisibilityCheck vs FieldVisibilityCheck).
   */
  private get evaluatesHiddenSources(): boolean {
    return (
      !this.isButtonSetting &&
      (this.fieldSetting as VisibilityFormField).fieldType ===
        SECTION_FIELD_TYPE
    );
  }

  public get field(): TFieldSetting {
    return this.fieldSetting;
  }

  constructor(
    fieldSetting: TFieldSetting,
    conditionGroups: Array<Array<VisibilityConditionBase>>,
    fieldSettingsFlatMap?: FieldSettingsLookup,
  ) {
    this.fieldSetting = fieldSetting;
    this.conditionGroups = conditionGroups;
    this.fieldSettingsFlatMap = fieldSettingsFlatMap;
    //  targetValueId array as strings instead of numbers.
    const conditions = conditionGroups.flat();
    const entityIdConditions = conditions.filter(
      (condition) =>
        condition.fieldCondition.targetValueType === 'ENTITY_ID' &&
        isArrayOf(condition.fieldCondition.targetValueId, 'string'),
    );
    if (entityIdConditions.length > 0) {
      for (let i = 0, length = entityIdConditions.length; i < length; i++) {
        const condition = entityIdConditions[i];
        const targetValueWritable = Object.getOwnPropertyDescriptor(
          condition.fieldCondition,
          'targetValueId',
        )?.writable;
        if (
          Array.isArray(condition.fieldCondition.targetValueId) &&
          targetValueWritable === true
        ) {
          const numberArray =
            condition.fieldCondition.targetValueId.map(Number);
          condition.fieldCondition.targetValueId = [...numberArray];
        }
      }
    }
  }

  /**
   * Runs the visibility condition groups to determine if a field should be visible.
   * @param {JsonDataItem} data - The updated data used to determine if the field should be visible.
   * @param {JsonData} groups - The user groups used to determine if the field should be visible.
   * @param {FieldSetting} parentFieldSetting - If the parent field setting is hidden then these conditions will not get run, unless this setting is a section.
   */
  public setFieldVisibility(
    data: JsonDataItem,
    groups?: JsonData,
    parentFieldSetting?: FieldSetting,
  ) {
    let currentHiddenValue = true;
    const parentBlocksEvaluation =
      !this.evaluatesHiddenSources &&
      parentFieldSetting?.fieldSetting.hidden === true;

    if (!parentBlocksEvaluation) {
      for (let i = 0, length = this.conditionGroups.length; i < length; i++) {
        if (i === 0) {
          // the first group always runs
          currentHiddenValue = this.shouldBeHidden(
            this.conditionGroups[i],
            data,
            groups,
          );
        } else {
          // each subsequent group runs only if the previous group was successful, if not it breaks out of the loop
          if (!currentHiddenValue) {
            currentHiddenValue = this.shouldBeHidden(
              this.conditionGroups[i],
              data,
              groups,
            );
          } else {
            break;
          }
        }
      }
    }

    this._hasSettingChanged = this.fieldSetting.hidden !== currentHiddenValue;
    this.fieldSetting.hidden = currentHiddenValue;
  }

  /**
   * Runs a series of conditions to determine if the result would be to show the field or button.
   * @param {Array<VisibilityConditionBase>} conditions - The visibility conditions to evaluate.
   * @param {JsonDataItem} data - The data used to evaluate the conditions.
   * @param {JsonData} groups - The user groups used to evaluate the conditions.
   * @returns {boolean} Determines if the conditions would cause a field or button to be hidden or not.
   * @private
   */
  private shouldBeHidden(
    conditions: Array<VisibilityConditionBase>,
    data: JsonDataItem,
    groups?: JsonData,
  ): boolean {
    let isHidden = true;

    for (let i = 0, length = conditions.length; i < length; i++) {
      const condition = conditions[i];
      const sourceSetting = this.getSourceFieldSetting(condition);
      const sourceIsHidden = sourceSetting?.hidden === true;
      const sourceIsReadDenied = sourceSetting?.permissions?.read === false;

      if (
        sourceIsHidden &&
        sourceIsReadDenied &&
        this.fieldSetting?.isVisibleForced !== undefined
      ) {
        // Target has a forced visibility override from the server. Respect it
        // and short-circuit so no subsequent condition can overwrite the forced result.
        isHidden = !this.fieldSetting.isVisibleForced;
        break;
      }

      // A hidden source field forces a dependent field hidden, but never a dependent section.
      // Sections always evaluate. Read-denied sources are excepted: they are hidden by permission
      // defaults (e.g. new records the server hasn't confirmed yet) and may still hold a value.
      if (
        sourceIsHidden &&
        !sourceIsReadDenied &&
        !this.evaluatesHiddenSources
      ) {
        if (
          conditions.length === 1 ||
          condition.fieldCondition.conjugate === 'AND'
        ) {
          // Force hidden; an earlier condition might have set it to visible.
          isHidden = true;
          break;
        }

        if (!this.runNextCondition(isHidden, condition.fieldCondition)) {
          break;
        }

        continue;
      }

      isHidden = this.evaluateCondition(condition, data, groups);

      if (!this.runNextCondition(isHidden, condition.fieldCondition)) {
        break;
      }
    }

    return isHidden;
  }

  /**
   * Looks up the form setting of the field a condition reads from.
   * @param {VisibilityConditionBase} condition - The condition whose source field to resolve.
   * @returns {VisibilityFormField | undefined} The source field setting, if it is in the flat map.
   * @private
   */
  private getSourceFieldSetting(
    condition: VisibilityConditionBase,
  ): VisibilityFormField | undefined {
    const sourceFieldId = condition.sourceFieldId;

    if (!this.fieldSettingsFlatMap || !sourceFieldId) {
      return undefined;
    }

    return this.fieldSettingsFlatMap.get(sourceFieldId);
  }

  /**
   * Runs a single condition against the data it applies to. GROUP conditions check user group
   * membership, so they read the user groups rather than the form data.
   * @param {VisibilityConditionBase} condition - The condition to evaluate.
   * @param {JsonDataItem} data - The form data.
   * @param {JsonData} groups - The user groups.
   * @returns {boolean} Whether this condition on its own would hide the field.
   * @private
   */
  private evaluateCondition(
    condition: VisibilityConditionBase,
    data: JsonDataItem,
    groups?: JsonData,
  ): boolean {
    if (condition.VisibilityType !== 'GROUP') {
      return condition.runVisibilityCondition(data);
    }

    return groups ? condition.runVisibilityCondition(groups) : true;
  }

  /**
   * Determines if the next visibility condition should be run. The purpose of visibility condition(s) is to set a field
   * to visible, with that understanding here is how isHidden in evaluated.
   *
   * 1. If the current isHidden value is true and the conjugate is AND then there is no reason to run the other
   * conditions because all conditions must be met for the field to be set visible.
   *
   * 2. If the current isHidden value is false and the conjugate is OR then there is no reason to run the other
   * conditions because only one of them must be met for the field to be set visible.
   *
   * @param {boolean} currentHiddenValue - The current hidden field value.
   * @param {VisibilityCondition} condition - The current condition to check.
   * @returns {boolean} Determines if the next visibility condition should be run.
   * @private
   */
  private runNextCondition(
    currentHiddenValue: boolean,
    condition: VisibilityCondition,
  ): boolean {
    if (currentHiddenValue && condition.conjugate === 'AND') {
      return false;
    } else if (!currentHiddenValue && condition.conjugate === 'OR') {
      return false;
    }

    return true;
  }

  private isFormButton(type: TFieldSetting) {
    return (type as VisibilityButtonSetting).name !== undefined;
  }
}
